import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon, PlayIcon, CheckCircleIcon, ExclamationCircleIcon, QuestionMarkCircleIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { awsService, AWSCredential } from '../../services/awsService';
import Toast, { ToastType } from '../Toast';

interface SyncPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSyncComplete: () => void;
}

const SyncPanel: React.FC<SyncPanelProps> = ({ isOpen, onClose, onSyncComplete }) => {
  const [credentials, setCredentials] = useState<AWSCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [syncingCredentialId, setSyncingCredentialId] = useState<number | null>(null);
  const [syncProgress, setSyncProgress] = useState<{ [key: number]: string }>({});
  const [testingCredentialId, setTestingCredentialId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [newCredential, setNewCredential] = useState({
    credentialName: '',
    accessKeyId: '',
    secretAccessKey: '',
    region: 'us-east-1'
  });

  useEffect(() => {
    if (isOpen) {
      loadCredentials();
    }
  }, [isOpen]);

  const loadCredentials = async () => {
    try {
      const { credentials: creds } = await awsService.getCredentials();
      setCredentials(creds);
      // Automatically show add form if no credentials exist
      if (creds.length === 0) {
        setShowAddForm(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const isFirstCredential = credentials.length === 0;
      const addedCredential = await awsService.addCredential(newCredential);
      setSuccess('Credential added successfully');
      setNewCredential({
        credentialName: '',
        accessKeyId: '',
        secretAccessKey: '',
        region: 'us-east-1'
      });
      setShowAddForm(false);
      await loadCredentials();

      // If this was the first credential, automatically start syncing
      if (isFirstCredential && addedCredential.credential) {
        setToast({ message: 'Starting initial sync...', type: 'info' });
        // Small delay to let the UI update
        setTimeout(() => {
          handleSyncResources(addedCredential.credential.id, true);
        }, 500);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add credential');
    }
  };

  const handleDeleteCredential = async (id: number) => {
    try {
      // First, check if there are resources associated with this credential
      const response = await awsService.deleteCredential(id);

      // Check if the response contains a warning about resources
      if (response && typeof response === 'object' && 'warning' in response) {
        const warningResponse = response as any;
        const totalResources = warningResponse.resourceBreakdown ?
          Object.values(warningResponse.resourceBreakdown).reduce((sum: number, count: any) => sum + count, 0) : 0;

        const confirmMessage = totalResources > 0 ?
          `This credential has ${totalResources} associated resources tracked in X-AWS.\n\n` +
          `Resource breakdown:\n` +
          Object.entries(warningResponse.resourceBreakdown || {})
            .filter(([_, count]) => count as number > 0)
            .map(([key, count]) => `- ${key}: ${count}`)
            .join('\n') +
          `\n\nDeleting this credential will remove this data from X-AWS only.\n` +
          `Your actual AWS resources will NOT be affected.\n\n` +
          `Are you sure you want to delete this credential?` :
          'Are you sure you want to delete this credential?';

        if (!window.confirm(confirmMessage)) return;

        // User confirmed, proceed with force delete
        await awsService.deleteCredential(id, true);
      }

      setSuccess('Credential deleted successfully');
      await loadCredentials();

      // If this was the last credential, refresh the entire dashboard
      const { credentials: remainingCreds } = await awsService.getCredentials();
      if (remainingCreds.length === 0) {
        // Close the modal and refresh the dashboard to show onboarding
        onClose();
        onSyncComplete();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete credential');
    }
  };

  const handleTestCredential = async (id: number) => {
    setTestingCredentialId(id);
    try {
      const result = await awsService.testCredential(id);
      if (result.success) {
        setSuccess(`Credential test successful! Account: ${result.accountId}`);
      } else {
        setError(result.error || 'Credential test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to test credential');
    } finally {
      setTestingCredentialId(null);
    }
  };

  const handleSyncResources = async (credentialId: number, useUnifiedSync: boolean = false) => {
    setSyncingCredentialId(credentialId);
    setError('');
    setSuccess('');

    try {
      if (useUnifiedSync) {
        // Use unified sync for resources + bandwidth
        setSyncProgress({ [credentialId]: 'Syncing resources...' });
        setToast({ message: 'Starting unified sync (resources + bandwidth)...', type: 'info' });

        const regions = ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
          'eu-west-1', 'eu-west-2', 'eu-central-1',
          'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'];

        // Call unified sync endpoint
        const result = await awsService.unifiedSync(credentialId, regions);

        if (result.success) {
          setToast({ message: 'Sync completed successfully!', type: 'success' });
          setSyncProgress({ [credentialId]: 'Sync complete!' });

          // Refresh the dashboard
          onSyncComplete();

          // Auto-refresh page after 2 seconds
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } else {
        // Original resource-only sync
        setSyncProgress({ [credentialId]: 'Syncing resources...' });
        setToast({ message: 'Starting resource sync...', type: 'info' });

        await awsService.syncAllResources(credentialId);

        setToast({ message: 'Resources synced successfully!', type: 'success' });
        setSyncProgress({ [credentialId]: 'Sync complete!' });

        // Refresh the dashboard
        onSyncComplete();

        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to start sync';
      setToast({ message: errorMessage, type: 'error' });
      setSyncProgress({});
    } finally {
      setSyncingCredentialId(null);
      // Clear progress after 3 seconds
      setTimeout(() => setSyncProgress({}), 3000);
    }
  };

  const regions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-2xl transition-all border border-gray-200 dark:border-gray-700">
            <Dialog.Title
              as="h3"
              className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <span>IAM Key Management</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-11">
                    Tip: Use read-only IAM keys for enhanced security
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </Dialog.Title>

            <div className="mt-4">
              {/* Error/Success Messages */}
              {error && (
                <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/50 p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3 text-sm text-red-700 dark:text-red-300">
                      {error}
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 rounded-md bg-green-50 dark:bg-green-900/50 p-4">
                  <div className="flex">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    <div className="ml-3 text-sm text-green-700 dark:text-green-300">
                      {success}
                    </div>
                  </div>
                </div>
              )}

              {/* Credentials List */}
              <div className="space-y-4">
                {credentials.length > 0 && (
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Existing Credentials
                    </h4>
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Credential
                    </button>
                  </div>
                )}

                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  </div>
                ) : credentials.length > 0 ? (
                  <div className="space-y-3">
                    {credentials.map((credential) => (
                      <div
                        key={credential.id}
                        className="bg-slate-950 border border-slate-800 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium text-white">
                              {credential.credentialName}
                            </h5>
                            <p className="text-sm text-gray-300">
                              Region: {credential.region}
                            </p>
                            {credential.lastSync && (
                              <p className="text-xs text-gray-400">
                                Last synced: {new Date(credential.lastSync).toLocaleString()}
                              </p>
                            )}
                          </div>

                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleTestCredential(credential.id)}
                              disabled={testingCredentialId === credential.id}
                              className="inline-flex items-center px-2 py-1 border border-slate-700 text-xs font-medium rounded text-gray-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
                            >
                              {testingCredentialId === credential.id ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                              ) : (
                                <>
                                  <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Verify Key
                                </>
                              )}
                            </button>

                            <button
                              onClick={() => handleSyncResources(credential.id, true)}
                              disabled={syncingCredentialId === credential.id}
                              className="inline-flex items-center px-2 py-1 border border-slate-700 text-xs font-medium rounded text-gray-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
                              title="Unified Sync - Resources & Bandwidth"
                            >
                              {syncingCredentialId === credential.id ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1"></div>
                              ) : (
                                <ArrowPathIcon className="h-3 w-3 mr-1" />
                              )}
                              <span className="hidden sm:inline">Re-Sync</span>
                            </button>

                            <button
                              onClick={() => handleDeleteCredential(credential.id)}
                              className="inline-flex items-center px-2 py-1 border border-red-900 text-xs font-medium rounded text-red-400 bg-red-950/50 hover:bg-red-900/30"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        {syncProgress[credential.id] && (
                          <div className="mt-2 text-sm text-gray-300 flex items-center">
                            <div className="animate-pulse mr-2">●</div>
                            {syncProgress[credential.id]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Add Credential Form */}
                {showAddForm && (
                  <div className="border-2 border-indigo-200 dark:border-indigo-800 rounded-xl p-6 bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-indigo-900/20 shadow-lg">
                    <div className="flex items-center justify-between mb-6">
                      <h5 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2 animate-pulse"></div>
                        Add New AWS Credential
                      </h5>
                      <a
                        href="/documentation"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg transition-colors"
                      >
                        <QuestionMarkCircleIcon className="h-4 w-4 mr-1.5" />
                        Setup Instructions
                        <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                      </a>
                    </div>

                    {/* Quick Setup Callout */}
                    <div className="mb-6 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-semibold">Need AWS credentials? Try our 60-second setup!</p>
                          <p className="text-xs mt-1 text-indigo-100">Use our CloudFormation template to automatically create an IAM user with the right permissions.</p>
                          <a
                            href={`https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${encodeURIComponent('https://s3.amazonaws.com/x-aws/exitaws-readonly-setup.yaml')}&stackName=ExitAWS-ReadOnly-Setup`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-md transition-colors"
                          >
                            Launch Quick Setup
                            <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                          </a>
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleAddCredential} className="space-y-5">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Credential Name
                          </label>
                          <div className="relative rounded-lg shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </div>
                            <input
                              type="text"
                              required
                              value={newCredential.credentialName}
                              onChange={(e) => setNewCredential({ ...newCredential, credentialName: e.target.value })}
                              className="pl-10 pr-3 py-2 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm transition-colors"
                              placeholder="e.g., Production AWS"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Access Key ID
                          </label>
                          <div className="relative rounded-lg shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </div>
                            <input
                              type="text"
                              required
                              value={newCredential.accessKeyId}
                              onChange={(e) => setNewCredential({ ...newCredential, accessKeyId: e.target.value })}
                              className="pl-10 pr-3 py-2 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm font-mono transition-colors"
                              placeholder="AKIAXXXXXXXXXXXXXXXX"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Secret Access Key
                          </label>
                          <div className="relative rounded-lg shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                            <input
                              type="password"
                              required
                              value={newCredential.secretAccessKey}
                              onChange={(e) => setNewCredential({ ...newCredential, secretAccessKey: e.target.value })}
                              className="pl-10 pr-3 py-2 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm font-mono transition-colors"
                              placeholder="••••••••••••••••••••••••••••••••••••••••"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Default Region
                          </label>
                          <div className="relative rounded-lg shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <select
                              value={newCredential.region}
                              onChange={(e) => setNewCredential({ ...newCredential, region: e.target.value })}
                              className="pl-10 pr-3 py-2 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm transition-colors"
                            >
                              {regions.map((region) => (
                                <option key={region} value={region}>
                                  {region}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => setShowAddForm(false)}
                          className="px-5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center"
                        >
                          <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Credential
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Dialog>
  );
};

export default SyncPanel;